import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useStytchMemberSession } from "@stytch/react/b2b";
import {
  ALL_LEVELS,
  LEVEL_LABEL,
  ROLE_FOR_LEVEL,
  type Member,
  type PermissionLevel,
  type StytchRoleId,
} from "../../services/adminService";

const LEVEL_ICON: Record<PermissionLevel, string> = {
  admin: "admin_panel_settings",
  manager: "supervisor_account",
  member: "person",
};

export interface MemberRowProps {
  member: Member;
  organizations: Record<string, string>;
  onChangeRole: (member: Member, nextRole: StytchRoleId) => void;
  onDeactivate: (member: Member) => void;
  onCancelInvite: (member: Member) => void;
}

/**
 * MemberRow — single row in the admin member table.
 *
 * Renders 6 cells (Email, Name, Organization, Role, Status, Kebab). The kebab trigger is
 * omitted for the admin's own row as a UX nicety — the backend
 * (AdminService.assertNotSelf) enforces self-protection regardless.
 *
 * Action set varies by row type:
 *   - Active, not self: Change Role + Deactivate (destructive)
 *   - Pending, not self: Cancel Invite (destructive)
 *   - Self: no kebab rendered
 *
 * Self-detection coupling:
 *   The UI compares member.member_id (Stytch ID from the list endpoint) to
 *   session.member_id (Stytch ID from the active Stytch session). The backend
 *   assertNotSelf also keys off Stytch member_id (resolves the requester's
 *   stytch_member_id and compares to the path-param target). The two layers
 *   therefore use the *same* identity space — a future Stytch session shape
 *   change that drops `member_id` would break client gating without affecting
 *   server enforcement. If that ever happens, thread the user's
 *   stytch_member_id through a server endpoint (e.g. GET /me) so the UI no
 *   longer reads it from the Stytch session directly.
 */
const MemberRow: React.FC<MemberRowProps> = ({
  member,
  organizations,
  onChangeRole,
  onDeactivate,
  onCancelInvite,
}) => {
  const { session } = useStytchMemberSession();
  const selfStytchMemberId = session?.member_id;
  const isSelf = member.member_id === selfStytchMemberId;
  const isPending = member.status !== "active";

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Trigger ref + menuPos let us portal the menu out of the table's
  // overflow-hidden container so it isn't clipped on last-row kebab opens.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);

  // Position the menu relative to the viewport using the trigger's bounding rect.
  // useLayoutEffect runs synchronously after DOM mutations and before paint, so
  // there's no visible flash of the menu in the wrong position.
  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) {
      setMenuPos(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4, // 4px = mt-1
      right: window.innerWidth - rect.right,
    });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideMenu = menuRef.current?.contains(target);
      const insideTrigger = triggerRef.current?.contains(target);
      if (!insideMenu && !insideTrigger) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    // Re-position the menu on viewport changes so the portal stays anchored.
    const handleReposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [menuOpen]);

  const roleBadgeBase =
    "text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded border";
  const roleBadgeTone: Record<PermissionLevel, string> = {
    admin: "bg-primary/10 text-primary border-primary/30",
    manager: "bg-secondary/10 text-secondary border-secondary/30",
    member: "bg-tertiary/10 text-tertiary border-tertiary/30",
  };
  const roleBadgeClasses = `${roleBadgeBase} ${roleBadgeTone[member.level]}`;

  const statusPillClasses =
    member.status === "active"
      ? "text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
      : "text-xs font-label font-bold tracking-widest uppercase px-2 py-1 rounded bg-outline-variant/30 text-on-surface-variant border border-outline-variant/50";

  return (
    <tr className="border-b border-outline-variant/10 hover:bg-surface-container-high transition-colors">
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        <span className="truncate block max-w-xs">{member.email_address}</span>
      </td>
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        {isPending ? (
          <span className="text-on-surface-variant">—</span>
        ) : (
          member.name || <span className="text-on-surface-variant">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        <span className="truncate block max-w-xs">
          {organizations[member.organization_id] ?? member.organization_id}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        <span className={roleBadgeClasses}>{LEVEL_LABEL[member.level]}</span>
      </td>
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        <span className={statusPillClasses}>
          {isPending ? "Pending" : "Active"}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-body text-on-surface">
        {!isSelf && (
          <>
            <button
              ref={triggerRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) => !prev);
              }}
              className="p-2 rounded text-slate-500 hover:text-on-surface hover:bg-surface-container-highest transition-colors"
              aria-label="Member actions"
            >
              <span className="material-symbols-outlined text-base">more_vert</span>
            </button>
            {menuOpen && menuPos &&
              createPortal(
                <div
                  ref={menuRef}
                  style={{ position: "fixed", top: menuPos.top, right: menuPos.right }}
                  className="z-[60] bg-surface-container-high border border-outline-variant/30 rounded shadow-xl py-1 min-w-[160px]"
                >
                  {isPending ? (
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onCancelInvite(member);
                      }}
                      className="w-full px-3 py-2 text-left text-xs font-label text-error hover:bg-surface-container flex items-center gap-2 transition-colors"
                    >
                      <span className="material-symbols-outlined text-sm">mail_off</span>
                      Cancel Invite
                    </button>
                  ) : (
                    <>
                      {/*
                        One entry per tier the member is not already at.
                        Stytch's `roles` is a full replacement, so each of
                        these sets the target tier outright rather than
                        adding to what they hold — which is also why a
                        two-state toggle was wrong: moving a manager to
                        admin and back landed them on `stytch_member` and
                        destroyed the manager role without saying so.
                      */}
                      {ALL_LEVELS.filter((l) => l !== member.level).map((l) => (
                        <button
                          key={l}
                          onClick={() => {
                            setMenuOpen(false);
                            onChangeRole(member, ROLE_FOR_LEVEL[l]);
                          }}
                          className="w-full px-3 py-2 text-left text-xs font-label text-slate-300 hover:bg-surface-container flex items-center gap-2 transition-colors"
                        >
                          <span className="material-symbols-outlined text-sm">
                            {LEVEL_ICON[l]}
                          </span>
                          {`Change to ${LEVEL_LABEL[l]}`}
                        </button>
                      ))}
                      <div className="border-t border-outline-variant/20 my-1" />
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          onDeactivate(member);
                        }}
                        className="w-full px-3 py-2 text-left text-xs font-label text-error hover:bg-surface-container flex items-center gap-2 transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">person_remove</span>
                        Deactivate
                      </button>
                    </>
                  )}
                </div>,
                document.body,
              )}
          </>
        )}
      </td>
    </tr>
  );
};

export default MemberRow;
